import React, { useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid2 as Grid,
  Chip,
  CircularProgress,
  Alert,
  FormHelperText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Controller } from 'react-hook-form';

import DescribedField from './DescribedField';
import BlockList from './blocks/BlockList';

/**
 * SiteForm — Formulário do editor de sites multi-agenda.
 *
 * Organiza a entrada em 6 accordions:
 *   1. Identidade (SEO)
 *   2. Escopo (professional/places/schedules)
 *   3. Template (template choice + layoutMode)
 *   4. Estilos (colors, fonts, borders)
 *   5. Blocos (via BlockList)
 *
 * Props:
 *   @param {object} form — instância de useForm (RHF)
 *   @param {string} siteType — 'link_in_bio' | 'professional_site' | 'company_site'
 *   @param {object[]} templates — templates disponíveis para layoutTemplateId
 *   @param {object} context — contexto com availableProfessionals, availablePlaces, etc.
 *
 * O componente NÃO submete; apenas renderiza e controla via RHF.
 * Obrigação O11: cada campo tem descrição ≥1 parágrafo.
 */
export default function SiteForm({
  form,
  siteType = 'link_in_bio',
  templates = [],
  context = {},
}) {
  const { control, watch } = form;
  const layoutTemplateId = watch('layoutTemplateId');
  const layoutMode = watch('layoutMode');

  // Hook para gerenciar blocks — NOT USED DIRECTLY HERE, mas pode ser passado ao BlockList
  // O SiteForm apenas renderiza; o gerenciamento de blocks é via RHF Controller
  const blocks = watch('blocks') || [];

  // Quando o template muda, validar e restringir layoutMode aos modos suportados
  useEffect(() => {
    if (!layoutTemplateId) return;

    // Encontrar o template selecionado
    const selectedTemplate = templates.find((t) => t.id === layoutTemplateId);
    if (!selectedTemplate) return;

    // Obter modos suportados (default: ["continuous"])
    const supportedModes = selectedTemplate.supportedModes || ['continuous'];

    // Se o modo atual não está suportado, resetar para o primeiro suportado
    if (layoutMode && !supportedModes.includes(layoutMode)) {
      form.setValue('layoutMode', supportedModes[0], { shouldDirty: true });
    }
  }, [layoutTemplateId, layoutMode, templates, form]);

  // Computar modos suportados do template atual para filtrar as opções na UI
  const supportedLayoutModes = useMemo(() => {
    if (!layoutTemplateId) return ['menu', 'tabs', 'continuous'];
    const selectedTemplate = templates.find((t) => t.id === layoutTemplateId);
    return selectedTemplate?.supportedModes || ['continuous'];
  }, [layoutTemplateId, templates]);

  // Mapa de descrições para cada modo
  const modeDescriptions = {
    menu: 'Menu Lateral/Topo',
    tabs: 'Abas',
    continuous: 'Contínuo (Scroll)',
  };

  const handleBlockUpdate = (blockId, newConfig) => {
    const idx = blocks.findIndex((b) => b.id === blockId);
    if (idx >= 0) {
      form.setValue(`blocks.${idx}.config`, newConfig, { shouldDirty: true });
    }
  };

  const handleBlockToggle = (blockId) => {
    const idx = blocks.findIndex((b) => b.id === blockId);
    if (idx >= 0) {
      form.setValue(`blocks.${idx}.enabled`, !blocks[idx].enabled, { shouldDirty: true });
    }
  };

  const handleBlockReorder = (fromIndex, toIndex) => {
    // Work on sorted order (same view as BlockList) then persist entire array
    const sorted = [...blocks].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const reordered = [...sorted];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    form.setValue(
      'blocks',
      reordered.map((b, i) => ({ ...b, order: i })),
      { shouldDirty: true }
    );
  };

  const handleBlockAdd = (type) => {
    const newBlock = {
      id: crypto.randomUUID(), // UUID estável — nunca alterado (O8)
      type,
      enabled: true,
      order: blocks.length,
      config: {},
    };
    form.setValue('blocks', [...blocks, newBlock], { shouldDirty: true });
  };

  const handleBlockRemove = (blockId) => {
    const remaining = blocks.filter((b) => b.id !== blockId);
    form.setValue('blocks', remaining.map((b, i) => ({ ...b, order: i })), { shouldDirty: true });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Accordion 1: Identidade (SEO) */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight={600}>
            Identidade
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Controller
            name="seo.title"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DescribedField
                label="Título da Página"
                description="Título exibido no navegador e mecanismos de busca. Máx. 120 caracteres. Use o nome do profissional ou empresa."
                error={!!error}
                helperText={error?.message}
              >
                <TextField
                  {...field}
                  placeholder="ex: Dra. João Silva - Dermatologia"
                  fullWidth
                  size="small"
                  variant="outlined"
                  inputProps={{ maxLength: 120 }}
                />
              </DescribedField>
            )}
          />

          <Controller
            name="seo.description"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DescribedField
                label="Descrição SEO"
                description="Texto exibido nos resultados de busca e redes sociais. Máx. 300 caracteres. Resuma a proposta da página."
                error={!!error}
                helperText={error?.message}
              >
                <TextField
                  {...field}
                  placeholder="ex: Consultório especializado em dermatologia clínica e estética."
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  variant="outlined"
                  inputProps={{ maxLength: 300 }}
                />
              </DescribedField>
            )}
          />

          <Controller
            name="seo.ogImageUrl"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DescribedField
                label="Imagem OG (URL)"
                description="URL da imagem exibida ao compartilhar a página em redes sociais. Deixe vazio para usar imagem padrão."
                error={!!error}
                helperText={error?.message}
              >
                <TextField
                  {...field}
                  placeholder="https://..."
                  fullWidth
                  size="small"
                  variant="outlined"
                />
              </DescribedField>
            )}
          />
        </AccordionDetails>
      </Accordion>

      {/* Accordion 2: Escopo */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight={600}>
            Escopo
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {siteType === 'professional_site' && (
            <Alert severity="info" icon={false}>
              Em páginas profissionais, o profissional é fixo (definido ao criar a página).
            </Alert>
          )}

          {siteType === 'company_site' && (
            <>
              <Controller
                name="scope.placeIds"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DescribedField
                    label="Locais"
                    description="Selecione quais locais aparecem nesta página. Os locais são derivados das agendas escolhidas abaixo."
                    error={!!error}
                    helperText={error?.message}
                  >
                    <Select
                      multiple
                      {...field}
                      size="small"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {selected.map((v) => (
                            <Chip
                              key={v}
                              label={context.availablePlaces?.find((p) => p.id === v)?.name || v}
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {(context.availablePlaces || []).map((place) => (
                        <MenuItem key={place.id} value={place.id}>
                          {place.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </DescribedField>
                )}
              />
            </>
          )}

          <Controller
            name="scope.scheduleIds"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DescribedField
                label="Agendas"
                description="Selecione quais agendas aparecem nesta página. Para criar ou editar agendas, vá para a página de Agendas."
                error={!!error}
                helperText={error?.message}
              >
                <Select
                  multiple
                  {...field}
                  size="small"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {selected.map((v) => (
                        <Chip
                          key={v}
                          label={context.availableSchedules?.find((s) => s.id === v)?.name || v}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                >
                  {(context.availableSchedules || []).map((sched) => (
                    <MenuItem key={sched.id} value={sched.id}>
                      {sched.name}
                    </MenuItem>
                  ))}
                </Select>
              </DescribedField>
            )}
          />
        </AccordionDetails>
      </Accordion>

      {/* Accordion 3: Template */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight={600}>
            Template & Layout
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Controller
            name="layoutTemplateId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DescribedField
                label="Template Visual"
                description="Escolha o design e estrutura visual da página. Cada template pode suportar diferentes modos de layout (menu/abas/contínuo)."
                error={!!error}
                helperText={error?.message}
              >
                <FormControl fullWidth size="small">
                  <InputLabel>Template</InputLabel>
                  <Select
                    {...field}
                    label="Template"
                    renderValue={(v) => templates.find((t) => t.id === v)?.name || v}
                  >
                    {templates.map((tpl) => (
                      <MenuItem key={tpl.id} value={tpl.id}>
                        {tpl.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </DescribedField>
            )}
          />

          <Controller
            name="layoutMode"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DescribedField
                label="Modo de Layout"
                description="Como os blocos são exibidos: Menu fixo lateral/topo com navegação, Abas independentes, ou Contínuo em scroll linear."
                error={!!error}
                helperText={error?.message}
              >
                <FormControl fullWidth size="small">
                  <InputLabel>Modo</InputLabel>
                  <Select {...field} label="Modo">
                    {supportedLayoutModes.includes('menu') && (
                      <MenuItem value="menu">{modeDescriptions.menu}</MenuItem>
                    )}
                    {supportedLayoutModes.includes('tabs') && (
                      <MenuItem value="tabs">{modeDescriptions.tabs}</MenuItem>
                    )}
                    {supportedLayoutModes.includes('continuous') && (
                      <MenuItem value="continuous">{modeDescriptions.continuous}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </DescribedField>
            )}
          />
        </AccordionDetails>
      </Accordion>

      {/* Accordion 4: Estilos */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight={600}>
            Estilos
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={1}>
            {[
              { name: 'customStyles.primaryColor', label: 'Cor Primária', default: '#1976d2' },
              { name: 'customStyles.backgroundColor', label: 'Cor de Fundo', default: '#ffffff' },
              { name: 'customStyles.textColor', label: 'Cor de Texto', default: '#212121' },
              { name: 'customStyles.accentColor', label: 'Cor de Destaque', default: '#f50057' },
            ].map(({ name, label, default: dflt }) => (
              <Grid key={name} size={{ xs: 12, sm: 6 }}>
                <Controller
                  name={name}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label={label}
                      type="color"
                      fullWidth
                      size="small"
                      variant="outlined"
                      error={!!error}
                      helperText={error?.message || `Padrão: ${dflt}`}
                      inputProps={{ title: label }}
                    />
                  )}
                />
              </Grid>
            ))}
          </Grid>

          <Controller
            name="customStyles.fontFamily"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DescribedField
                label="Família de Fontes"
                description="Tipografia geral usada em toda a página."
                error={!!error}
                helperText={error?.message}
              >
                <FormControl fullWidth size="small">
                  <InputLabel>Fonte</InputLabel>
                  <Select {...field} label="Fonte">
                    <MenuItem value="Inter">Inter</MenuItem>
                    <MenuItem value="Roboto">Roboto</MenuItem>
                    <MenuItem value="Poppins">Poppins</MenuItem>
                    <MenuItem value="Montserrat">Montserrat</MenuItem>
                  </Select>
                </FormControl>
              </DescribedField>
            )}
          />

          <Grid container spacing={1}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="customStyles.fontSize"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DescribedField
                    label="Tamanho de Fonte"
                    description="Escala geral de texto da página."
                    error={!!error}
                    helperText={error?.message}
                  >
                    <FormControl fullWidth size="small">
                      <InputLabel>Tamanho</InputLabel>
                      <Select {...field} label="Tamanho">
                        <MenuItem value="small">Pequeno</MenuItem>
                        <MenuItem value="medium">Médio</MenuItem>
                        <MenuItem value="large">Grande</MenuItem>
                      </Select>
                    </FormControl>
                  </DescribedField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="customStyles.borderRadius"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DescribedField
                    label="Arredondamento"
                    description="Bordas dos elementos (cards, botões, etc)."
                    error={!!error}
                    helperText={error?.message}
                  >
                    <FormControl fullWidth size="small">
                      <InputLabel>Raio</InputLabel>
                      <Select {...field} label="Raio">
                        <MenuItem value="none">Nenhum (Quadrado)</MenuItem>
                        <MenuItem value="soft">Suave</MenuItem>
                        <MenuItem value="round">Redondo</MenuItem>
                      </Select>
                    </FormControl>
                  </DescribedField>
                )}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Accordion 5: Blocos */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight={600}>
            Blocos ({blocks.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <BlockList
            blocks={blocks}
            siteType={siteType}
            onUpdate={handleBlockUpdate}
            onToggle={handleBlockToggle}
            onReorder={handleBlockReorder}
            onAdd={handleBlockAdd}
            onRemove={handleBlockRemove}
            context={context}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
